import React from 'react';
import { Photo, PhotoSelectionTooltipElement } from '../types/types';

interface PhotoSelectionTooltipProps {
  tooltip: PhotoSelectionTooltipElement;
  onPhotoSelect: (photoId: number, photoIndex: number) => void;
  onClose: () => void;
}

export const PhotoSelectionTooltip: React.FC<PhotoSelectionTooltipProps> = ({
  tooltip,
  onPhotoSelect,
  onClose,
}) => {
  if (!tooltip.visible) return null;

  // Sort photos by date (newest first)
  const sortedPhotos = [...tooltip.photos].sort((a, b) => {
    const [monthA, dayA, yearA] = a.date.split('.');
    const [monthB, dayB, yearB] = b.date.split('.');

    const dateA = new Date(2000 + parseInt(yearA), parseInt(monthA) - 1, parseInt(dayA));
    const dateB = new Date(2000 + parseInt(yearB), parseInt(monthB) - 1, parseInt(dayB));

    return dateB.getTime() - dateA.getTime();
  });

  // Calculate position to keep tooltip within viewport
  const adjustedPosition = { ...tooltip.position };
  const tooltipWidth = 200; // Approximate width
  const tooltipHeight = 250; // Approximate max height

  if (adjustedPosition.x + tooltipWidth / 2 > window.innerWidth) {
    adjustedPosition.x = window.innerWidth - tooltipWidth - 10;
  } else if (adjustedPosition.x - tooltipWidth / 2 < 0) {
    adjustedPosition.x = tooltipWidth / 2 + 10;
  }

  if (adjustedPosition.y + tooltipHeight > window.innerHeight) {
    adjustedPosition.y = tooltip.position.y - tooltipHeight - 10;
  }

  const handlePhotoClick = (photo: Photo, index: number) => {
    onPhotoSelect(photo.id, index);
    onClose();
  };
  const hasPhotos = sortedPhotos.length > 0;

  return (
    <div
      className="photo-selection-tooltip fixed z-[1000] bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[200px]"
      style={{
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`,
        transform: 'translateX(-50%)',
      }}
    >
      <div className="text-sm font-semibold text-foreground mb-2 pb-2 border-b border-border">
        {hasPhotos ? 'Available Photos' : 'No Photos'}
      </div>
      <div className="max-h-[200px] overflow-y-auto">
        {hasPhotos ? (
          sortedPhotos.map((photo, index) => (
            <div
              key={photo.id}
              onClick={(e) => {
                e.stopPropagation();
                handlePhotoClick(photo, index);
              }}
              className="flex justify-between px-3 py-2 cursor-pointer rounded hover:bg-accent transition-colors my-0.5"
              data-tour-id="iv-photo-tooltip-entry"
            >
              <span className="font-medium text-foreground">{photo.date}</span>
              <span className="text-muted-foreground text-[0.9em]">{photo.scan_type}</span>
            </div>
          ))
        ) : (
          <div className="px-3 py-2 text-sm text-muted-foreground">
            {tooltip.message || 'No photos available for this area yet.'}
          </div>
        )}
      </div>
    </div>
  );
}; 